import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  oauthConnectionsAPI,
  projectIntegrationsAPI,
  OauthConnection,
  ProjectIntegrations as ProjectIntegrationsType,
  GitHubRepo,
  GoogleDriveFolder,
  GoogleDriveSharedDrive,
  OneDriveDrive,
  OneDriveFolder,
  JiraSite,
  JiraProject,
} from '../../services/api';

interface ProjectIntegrationsProps {
  projectId: number;
  onUpdate?: () => void;
}

interface FolderPath {
  id: string;
  name: string;
}

const ProjectIntegrations: React.FC<ProjectIntegrationsProps> = ({
  projectId,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { confirm } = useConfirm();
  const [loading, setLoading] = useState(true);
  const [oauthConnections, setOauthConnections] = useState<OauthConnection[]>(
    [],
  );
  const [integrations, setIntegrations] =
    useState<ProjectIntegrationsType | null>(null);
  const [gitHubRepos, setGitHubRepos] = useState<GitHubRepo[]>([]);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Form states
  const [showGoogleDriveForm, setShowGoogleDriveForm] = useState(false);
  const [showGitHubForm, setShowGitHubForm] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [repoSearchQuery, setRepoSearchQuery] = useState('');
  const [showRepoDropdown, setShowRepoDropdown] = useState(false);
  const [gitHubOptions, setGitHubOptions] = useState({
    sync_commits: true,
    sync_prs: true,
    sync_issues: true,
    sync_code: true,
  });

  // Google Drive folder browser state
  const [googleDriveFolders, setGoogleDriveFolders] = useState<
    GoogleDriveFolder[]
  >([]);
  const [sharedDrives, setSharedDrives] = useState<GoogleDriveSharedDrive[]>(
    [],
  );
  const [currentDrive, setCurrentDrive] = useState<{
    id: string | null;
    name: string;
  }>({ id: null, name: 'My Drive' });
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [folderPath, setFolderPath] = useState<FolderPath[]>([
    { id: 'root', name: 'My Drive' },
  ]);
  const [selectedFolder, setSelectedFolder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showDriveSelector, setShowDriveSelector] = useState(true);

  // OneDrive state
  const [showOneDriveForm, setShowOneDriveForm] = useState(false);
  const [oneDriveDrives, setOneDriveDrives] = useState<OneDriveDrive[]>([]);
  const [oneDriveFolders, setOneDriveFolders] = useState<OneDriveFolder[]>([]);
  const [currentOneDriveDrive, setCurrentOneDriveDrive] = useState<{
    id: string | null;
    name: string;
  }>({ id: null, name: 'OneDrive' });
  const [loadingOneDriveFolders, setLoadingOneDriveFolders] = useState(false);
  const [oneDriveFolderPath, setOneDriveFolderPath] = useState<FolderPath[]>(
    [],
  );
  const [selectedOneDriveFolder, setSelectedOneDriveFolder] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [showOneDriveDriveSelector, setShowOneDriveDriveSelector] =
    useState(true);

  // Jira state
  const [showJiraForm, setShowJiraForm] = useState(false);
  const [jiraSites, setJiraSites] = useState<JiraSite[]>([]);
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraSite, setSelectedJiraSite] = useState<JiraSite | null>(
    null,
  );
  const [selectedJiraProject, setSelectedJiraProject] =
    useState<JiraProject | null>(null);
  const [loadingJiraSites, setLoadingJiraSites] = useState(false);
  const [loadingJiraProjects, setLoadingJiraProjects] = useState(false);
  const [jiraOptions, setJiraOptions] = useState({
    sync_issues: true,
    sync_sprints: true,
    sync_epics: true,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [connectionsRes, integrationsRes] = await Promise.all([
        oauthConnectionsAPI.getAll(),
        projectIntegrationsAPI.get(projectId),
      ]);

      setOauthConnections(connectionsRes.data.oauth_connections || []);
      setIntegrations(integrationsRes.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOAuthConnect = async (provider: string) => {
    try {
      const response = await oauthConnectionsAPI.authorize(provider);
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      const popup = window.open(
        response.data.authorization_url,
        `${provider}_oauth`,
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      // Poll for popup close
      const pollTimer = setInterval(() => {
        if (popup?.closed) {
          clearInterval(pollTimer);
          fetchData();
        }
      }, 500);
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to connect ${provider}`);
    }
  };

  const handleOAuthDisconnect = async (connectionId: number) => {
    const confirmed = await confirm({
      title: t('integrations.disconnectTitle'),
      message: t('integrations.confirmDisconnect'),
      confirmLabel: t('integrations.disconnect'),
      cancelLabel: t('common.cancel'),
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      await oauthConnectionsAPI.disconnect(connectionId);
      await fetchData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disconnect');
    }
  };

  const fetchGitHubRepos = async () => {
    try {
      setLoadingRepos(true);
      const response = await oauthConnectionsAPI.getGitHubRepos();
      setGitHubRepos(response.data.repos || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load repositories');
    } finally {
      setLoadingRepos(false);
    }
  };

  const fetchGoogleDriveFolders = async (
    parentId: string = 'root',
    driveId?: string | null,
  ) => {
    try {
      setLoadingFolders(true);
      const response = await oauthConnectionsAPI.getGoogleDriveFolders(
        parentId,
        driveId || undefined,
      );
      setGoogleDriveFolders(response.data.folders || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load folders');
    } finally {
      setLoadingFolders(false);
    }
  };

  const fetchSharedDrives = async () => {
    try {
      setLoadingFolders(true);
      const response = await oauthConnectionsAPI.getGoogleDriveSharedDrives();
      setSharedDrives(response.data.shared_drives || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load shared drives');
    } finally {
      setLoadingFolders(false);
    }
  };

  const handleDriveSelect = (driveId: string | null, driveName: string) => {
    console.log('handleDriveSelect:', { driveId, driveName });
    setCurrentDrive({ id: driveId, name: driveName });
    setShowDriveSelector(false);
    // Don't include drive in folderPath - it's stored separately in currentDrive
    setFolderPath([]);
    setSelectedFolder(null);
    fetchGoogleDriveFolders(driveId || 'root', driveId);
  };

  const handleFolderClick = (folder: GoogleDriveFolder) => {
    // Navigate into folder - use functional update to get latest state
    setFolderPath((prevPath) => [
      ...prevPath,
      { id: folder.id, name: folder.name },
    ]);
    setSelectedFolder(null);
    fetchGoogleDriveFolders(folder.id, currentDrive.id);
  };

  const handleFolderSelect = (folder: GoogleDriveFolder) => {
    setSelectedFolder({ id: folder.id, name: folder.name });
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      // Go back to drive selector
      setShowDriveSelector(true);
      setFolderPath([]);
      setSelectedFolder(null);
      setGoogleDriveFolders([]);
      return;
    }
    if (index === -2) {
      // Click on drive name - go back to drive root
      setFolderPath([]);
      setSelectedFolder(null);
      fetchGoogleDriveFolders(currentDrive.id || 'root', currentDrive.id);
      return;
    }
    const newPath = folderPath.slice(0, index + 1);
    setFolderPath(newPath);
    setSelectedFolder(null);
    fetchGoogleDriveFolders(newPath[newPath.length - 1].id, currentDrive.id);
  };

  const openGoogleDriveBrowser = () => {
    setShowGoogleDriveForm(true);
    setShowDriveSelector(true);
    setFolderPath([]);
    setSelectedFolder(null);
    setCurrentDrive({ id: null, name: 'My Drive' });
    setGoogleDriveFolders([]);
    fetchSharedDrives();
  };

  const handleConnectGoogleDrive = async () => {
    if (!selectedFolder) {
      setError(t('integrations.googleDrive.folderRequired'));
      return;
    }

    try {
      // Build full path: Folder1 / Folder2 / SelectedFolder (without drive name)
      const folderParts = [
        ...folderPath.map((f) => f.name),
        selectedFolder.name,
      ];
      const folderPathStr = folderParts.join(' / ');
      console.log('Saving:', {
        drive: currentDrive,
        folderPath: folderPathStr,
      });
      await projectIntegrationsAPI.connectGoogleDrive(projectId, {
        folder_id: selectedFolder.id,
        folder_name: selectedFolder.name,
        folder_path: folderPathStr,
        drive_id: currentDrive.id,
        drive_name: currentDrive.name,
        is_shared_drive: currentDrive.id !== null,
      });
      setShowGoogleDriveForm(false);
      setSelectedFolder(null);
      await fetchData();
      onUpdate?.();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          'Failed to connect Google Drive',
      );
    }
  };

  const handleConnectGitHub = async () => {
    if (!selectedRepo) {
      setError(t('integrations.github.repoRequired'));
      return;
    }

    const repo = gitHubRepos.find((r) => r.full_name === selectedRepo);

    try {
      await projectIntegrationsAPI.connectGitHub(projectId, {
        repo_full_name: selectedRepo,
        repo_name: repo?.name,
        default_branch: repo?.default_branch || 'main',
        ...gitHubOptions,
      });
      setShowGitHubForm(false);
      setSelectedRepo('');
      await fetchData();
      onUpdate?.();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to connect GitHub',
      );
    }
  };

  const handleDisconnectIntegration = async (
    type: 'google_drive' | 'onedrive' | 'github',
  ) => {
    const confirmed = await confirm({
      title: t('integrations.disconnectIntegrationTitle'),
      message: t('integrations.confirmDisconnectIntegration'),
      confirmLabel: t('integrations.disconnect'),
      cancelLabel: t('common.cancel'),
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      await projectIntegrationsAPI.disconnect(projectId, type);
      await fetchData();
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to disconnect integration');
    }
  };

  const handleDisconnectGitHubRepo = async (repoId: number) => {
    const confirmed = await confirm({
      title: t('integrations.disconnectIntegrationTitle'),
      message: t('integrations.confirmDisconnectIntegration'),
      confirmLabel: t('integrations.disconnect'),
      cancelLabel: t('common.cancel'),
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      await projectIntegrationsAPI.disconnect(projectId, 'github', repoId);
      await fetchData();
      onUpdate?.();
    } catch (err: any) {
      setError(
        err.response?.data?.error || 'Failed to disconnect GitHub repository',
      );
    }
  };

  const handleSync = async (
    type: 'google_drive' | 'onedrive' | 'github' | 'jira',
    integrationId?: number,
  ) => {
    try {
      const syncKey = integrationId ? `${type}-${integrationId}` : type;
      setSyncing(syncKey);
      await projectIntegrationsAPI.sync(projectId, type, integrationId);
      // Wait a bit and refresh
      setTimeout(() => {
        fetchData();
        setSyncing(null);
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start sync');
      setSyncing(null);
    }
  };

  // OneDrive handlers
  const fetchOneDriveDrives = async () => {
    try {
      setLoadingOneDriveFolders(true);
      const response = await oauthConnectionsAPI.getOneDriveDrives();
      setOneDriveDrives(response.data.drives || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load OneDrive drives');
    } finally {
      setLoadingOneDriveFolders(false);
    }
  };

  const fetchOneDriveFolders = async (parentId?: string, driveId?: string) => {
    try {
      setLoadingOneDriveFolders(true);
      const response = await oauthConnectionsAPI.getOneDriveFolders(
        parentId,
        driveId,
      );
      setOneDriveFolders(response.data.folders || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load OneDrive folders');
    } finally {
      setLoadingOneDriveFolders(false);
    }
  };

  const handleOneDriveDriveSelect = (driveId: string, driveName: string) => {
    setCurrentOneDriveDrive({ id: driveId, name: driveName });
    setShowOneDriveDriveSelector(false);
    setOneDriveFolderPath([]);
    setSelectedOneDriveFolder(null);
    fetchOneDriveFolders(undefined, driveId);
  };

  const handleOneDriveFolderClick = (folder: OneDriveFolder) => {
    setOneDriveFolderPath((prevPath) => [
      ...prevPath,
      { id: folder.id, name: folder.name },
    ]);
    setSelectedOneDriveFolder(null);
    fetchOneDriveFolders(folder.id, currentOneDriveDrive.id || undefined);
  };

  const handleOneDriveFolderSelect = (folder: OneDriveFolder) => {
    setSelectedOneDriveFolder({ id: folder.id, name: folder.name });
  };

  const handleOneDriveBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setShowOneDriveDriveSelector(true);
      setOneDriveFolderPath([]);
      setSelectedOneDriveFolder(null);
      setOneDriveFolders([]);
      return;
    }
    if (index === -2) {
      setOneDriveFolderPath([]);
      setSelectedOneDriveFolder(null);
      fetchOneDriveFolders(undefined, currentOneDriveDrive.id || undefined);
      return;
    }
    const newPath = oneDriveFolderPath.slice(0, index + 1);
    setOneDriveFolderPath(newPath);
    setSelectedOneDriveFolder(null);
    fetchOneDriveFolders(
      newPath[newPath.length - 1].id,
      currentOneDriveDrive.id || undefined,
    );
  };

  const openOneDriveBrowser = () => {
    setShowOneDriveForm(true);
    setShowOneDriveDriveSelector(true);
    setOneDriveFolderPath([]);
    setSelectedOneDriveFolder(null);
    setCurrentOneDriveDrive({ id: null, name: 'OneDrive' });
    setOneDriveFolders([]);
    fetchOneDriveDrives();
  };

  const handleConnectOneDrive = async () => {
    if (!selectedOneDriveFolder) {
      setError(t('integrations.onedrive.folderRequired'));
      return;
    }

    try {
      const folderParts = [
        ...oneDriveFolderPath.map((f) => f.name),
        selectedOneDriveFolder.name,
      ];
      const folderPathStr = folderParts.join(' / ');
      await projectIntegrationsAPI.connectOneDrive(projectId, {
        folder_id: selectedOneDriveFolder.id,
        folder_name: selectedOneDriveFolder.name,
        folder_path: folderPathStr,
        drive_id: currentOneDriveDrive.id || undefined,
      });
      setShowOneDriveForm(false);
      setSelectedOneDriveFolder(null);
      await fetchData();
      onUpdate?.();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to connect OneDrive',
      );
    }
  };

  // Jira handlers
  const fetchJiraSites = async () => {
    try {
      setLoadingJiraSites(true);
      const response = await oauthConnectionsAPI.getJiraSites();
      setJiraSites(response.data.sites || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Jira sites');
    } finally {
      setLoadingJiraSites(false);
    }
  };

  const fetchJiraProjects = async (cloudId: string) => {
    try {
      setLoadingJiraProjects(true);
      const response = await oauthConnectionsAPI.getJiraProjects(cloudId);
      setJiraProjects(response.data.projects || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Jira projects');
    } finally {
      setLoadingJiraProjects(false);
    }
  };

  const handleJiraSiteSelect = (site: JiraSite) => {
    setSelectedJiraSite(site);
    setSelectedJiraProject(null);
    fetchJiraProjects(site.id);
  };

  const openJiraBrowser = () => {
    setShowJiraForm(true);
    setSelectedJiraSite(null);
    setSelectedJiraProject(null);
    setJiraProjects([]);
    fetchJiraSites();
  };

  const handleConnectJira = async () => {
    if (!selectedJiraSite) {
      setError(t('integrations.jira.siteRequired'));
      return;
    }

    try {
      await projectIntegrationsAPI.connectJira(projectId, {
        cloud_id: selectedJiraSite.id,
        site_name: selectedJiraSite.name,
        site_url: selectedJiraSite.url,
        jira_project_id: selectedJiraProject?.id,
        jira_project_key: selectedJiraProject?.key,
        jira_project_name: selectedJiraProject?.name,
        ...jiraOptions,
      });
      setShowJiraForm(false);
      setSelectedJiraSite(null);
      setSelectedJiraProject(null);
      await fetchData();
      onUpdate?.();
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') || 'Failed to connect Jira',
      );
    }
  };

  const handleDisconnectJiraProject = async (integrationId: number) => {
    const confirmed = await confirm({
      title: t('integrations.disconnectIntegrationTitle'),
      message: t('integrations.confirmDisconnectIntegration'),
      confirmLabel: t('integrations.disconnect'),
      cancelLabel: t('common.cancel'),
      variant: 'warning',
    });
    if (!confirmed) return;

    try {
      await projectIntegrationsAPI.disconnect(projectId, 'jira', integrationId);
      await fetchData();
      onUpdate?.();
    } catch (err: any) {
      setError(
        err.response?.data?.error || 'Failed to disconnect Jira project',
      );
    }
  };

  const getConnectionForProvider = (provider: string) => {
    return oauthConnections.find((c) => c.provider === provider && c.active);
  };

  const formatLastSync = (dateStr?: string) => {
    if (!dateStr) return t('integrations.status.neverSynced');
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getSyncStatusBadge = (status?: string) => {
    if (isDark) {
      const statusColors: Record<string, string> = {
        pending: 'bg-amber-500/20 text-amber-400 border border-amber-500/30',
        syncing: 'bg-violet-500/20 text-violet-400 border border-violet-500/30',
        completed:
          'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
        failed: 'bg-rose-500/20 text-rose-400 border border-rose-500/30',
      };
      return (
        statusColors[status || 'pending'] ||
        'bg-slate-500/20 text-slate-400 border border-slate-500/30'
      );
    }
    const statusColors: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      syncing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
    };
    return statusColors[status || 'pending'] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div
          className={`px-4 py-3 rounded-2xl relative ${
            isDark
              ? 'bg-rose-500/10 border border-rose-500/30 text-rose-400'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <span>{error}</span>
          <button
            className={`absolute top-0 right-0 p-2 ${isDark ? 'text-rose-400 hover:text-rose-300' : 'text-red-600 hover:text-red-800'}`}
            onClick={() => setError(null)}
          >
            &times;
          </button>
        </div>
      )}

      {/* Google Drive Integration */}
      <div
        className={`rounded-2xl p-4 ${
          isDark
            ? 'bg-slate-900/50 border border-slate-700/50'
            : 'bg-white border border-slate-200 shadow-sm'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center space-x-3">
            <svg
              className="w-8 h-8 shrink-0"
              viewBox="0 0 87.3 78"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z"
                fill="#0066da"
              />
              <path
                d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
                fill="#00ac47"
              />
              <path
                d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
                fill="#ea4335"
              />
              <path
                d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
                fill="#00832d"
              />
              <path
                d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
                fill="#2684fc"
              />
              <path
                d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
                fill="#ffba00"
              />
            </svg>
            <div>
              <h3
                className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
              >
                {t('integrations.googleDrive.title')}
              </h3>
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
              >
                {t('integrations.googleDrive.description')}
              </p>
            </div>
          </div>

          {!getConnectionForProvider('google_drive') ? (
            <button
              onClick={() => handleOAuthConnect('google_drive')}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all"
            >
              {t('integrations.googleDrive.connect')}
            </button>
          ) : integrations?.google_drive ? (
            <div className="flex items-center space-x-2">
              <span
                className={`px-2 py-1 text-xs rounded-full ${getSyncStatusBadge(integrations.google_drive.sync_status)}`}
              >
                {t(
                  `integrations.status.${integrations.google_drive.sync_status || 'pending'}`,
                )}
              </span>
              <button
                onClick={() => handleSync('google_drive')}
                disabled={syncing === 'google_drive'}
                className={`px-3 py-1 text-sm rounded-lg disabled:opacity-50 transition-colors ${
                  isDark
                    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {syncing === 'google_drive'
                  ? t('integrations.status.syncing')
                  : t('integrations.actions.sync')}
              </button>
              <button
                onClick={() => handleDisconnectIntegration('google_drive')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  isDark
                    ? 'text-rose-400 border border-rose-500/50 hover:bg-rose-500/10'
                    : 'text-red-600 border border-red-300 hover:bg-red-50'
                }`}
              >
                {t('integrations.actions.disconnect')}
              </button>
            </div>
          ) : (
            <button
              onClick={openGoogleDriveBrowser}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all"
            >
              {t('integrations.googleDrive.selectFolder')}
            </button>
          )}
        </div>

        {integrations?.google_drive && (
          <div
            className={`mt-3 p-3 rounded-xl text-sm ${
              isDark ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}
          >
            <p
              className={`flex items-center gap-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
            >
              <strong>{t('integrations.googleDrive.drive')}:</strong>
              <span>
                {integrations.google_drive.drive_name ||
                  t('integrations.googleDrive.myDrive')}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  integrations.google_drive.is_shared_drive
                    ? isDark
                      ? 'bg-amber-500/20 text-amber-400'
                      : 'bg-yellow-100 text-yellow-800'
                    : isDark
                      ? 'bg-blue-500/20 text-blue-400'
                      : 'bg-blue-100 text-blue-800'
                }`}
              >
                {integrations.google_drive.is_shared_drive
                  ? t('integrations.googleDrive.sharedDrive')
                  : t('integrations.googleDrive.personalDrive')}
              </span>
            </p>
            <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>
              <strong>{t('integrations.googleDrive.folder')}:</strong>{' '}
              {integrations.google_drive.folder_path ||
                integrations.google_drive.folder_name ||
                integrations.google_drive.folder_id}
            </p>
            <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>
              <strong>{t('integrations.googleDrive.documents')}:</strong>{' '}
              {integrations.google_drive.synced_documents_count}
            </p>
            <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>
              <strong>{t('integrations.googleDrive.lastSync')}:</strong>{' '}
              {formatLastSync(integrations.google_drive.last_sync_at)}
            </p>
            {integrations.google_drive.last_sync_error && (
              <p className={isDark ? 'text-rose-400' : 'text-red-600'}>
                <strong>{t('integrations.error')}:</strong>{' '}
                {integrations.google_drive.last_sync_error}
              </p>
            )}
          </div>
        )}

        {showGoogleDriveForm && (
          <div
            className={`mt-4 p-4 rounded-xl ${
              isDark ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}
          >
            <h4
              className={`font-medium mb-3 ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
            >
              {t('integrations.googleDrive.browseAndSelect')}
            </h4>

            {showDriveSelector ? (
              /* Drive selector */
              <div
                className={`rounded-xl overflow-hidden ${
                  isDark
                    ? 'bg-slate-900/50 border border-slate-600/50'
                    : 'border bg-white'
                }`}
              >
                <div
                  className={`p-3 border-b ${isDark ? 'bg-slate-700/50 border-slate-600/50' : 'bg-gray-50'}`}
                >
                  <span
                    className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                  >
                    {t('integrations.googleDrive.selectDrive')}
                  </span>
                </div>
                {loadingFolders ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                  </div>
                ) : (
                  <ul
                    className={`divide-y ${isDark ? 'divide-slate-600/50' : 'divide-gray-100'}`}
                  >
                    {/* My Drive option */}
                    <li
                      className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                        isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => handleDriveSelect(null, 'My Drive')}
                    >
                      <div className="flex items-center gap-2">
                        <svg
                          className="w-5 h-5 text-blue-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                        <span
                          className={`text-sm ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                        >
                          {t('integrations.googleDrive.myDrive')}
                        </span>
                      </div>
                      <svg
                        className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </li>
                    {/* Shared Drives */}
                    {sharedDrives.length > 0 && (
                      <li
                        className={`p-2 ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}
                      >
                        <span
                          className={`text-xs font-medium uppercase ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                        >
                          {t('integrations.googleDrive.sharedDrives')}
                        </span>
                      </li>
                    )}
                    {sharedDrives.map((drive) => (
                      <li
                        key={drive.id}
                        className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                          isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => handleDriveSelect(drive.id, drive.name)}
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-5 h-5 text-yellow-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                            <path
                              fillRule="evenodd"
                              d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span
                            className={`text-sm ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                          >
                            {drive.name}
                          </span>
                        </div>
                        <svg
                          className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <>
                {/* Current drive indicator */}
                <div
                  className={`flex items-center gap-2 mb-3 p-2 rounded-xl ${
                    isDark ? 'bg-slate-600/50' : 'bg-gray-100'
                  }`}
                >
                  {currentDrive.id ? (
                    /* Shared Drive */
                    <svg
                      className="w-5 h-5 text-yellow-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                      <path
                        fillRule="evenodd"
                        d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    /* My Drive */
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                  )}
                  <span
                    className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                  >
                    {currentDrive.name}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${
                      currentDrive.id
                        ? isDark
                          ? 'bg-amber-500/20 text-amber-400'
                          : 'bg-yellow-100 text-yellow-800'
                        : isDark
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {currentDrive.id
                      ? t('integrations.googleDrive.sharedDrive')
                      : t('integrations.googleDrive.personalDrive')}
                  </span>
                  <button
                    onClick={() => handleBreadcrumbClick(-1)}
                    className={`ml-auto text-sm hover:underline ${
                      isDark
                        ? 'text-violet-400 hover:text-violet-300'
                        : 'text-indigo-600 hover:text-indigo-800'
                    }`}
                  >
                    {t('integrations.googleDrive.changeDrive')}
                  </button>
                </div>

                {/* Breadcrumb navigation */}
                <div
                  className={`flex items-center gap-1 text-sm mb-3 flex-wrap ${isDark ? 'text-slate-400' : 'text-gray-600'}`}
                >
                  <button
                    onClick={() => handleBreadcrumbClick(-2)}
                    className={`hover:underline ${isDark ? 'hover:text-violet-400' : 'hover:text-indigo-600'}`}
                  >
                    {currentDrive.name}
                  </button>
                  {/* Folder path */}
                  {folderPath.map((folder, index) => (
                    <React.Fragment key={folder.id}>
                      <span
                        className={isDark ? 'text-slate-500' : 'text-gray-400'}
                      >
                        /
                      </span>
                      <button
                        onClick={() => handleBreadcrumbClick(index)}
                        className={`hover:underline ${isDark ? 'hover:text-violet-400' : 'hover:text-indigo-600'}`}
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                {/* Folder list */}
                <div
                  className={`rounded-xl max-h-64 overflow-y-auto ${
                    isDark
                      ? 'bg-slate-900/50 border border-slate-600/50'
                      : 'border bg-white'
                  }`}
                >
                  {loadingFolders ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                    </div>
                  ) : googleDriveFolders.length === 0 ? (
                    <div
                      className={`py-8 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      {t('integrations.googleDrive.noFolders')}
                    </div>
                  ) : (
                    <ul
                      className={`divide-y ${isDark ? 'divide-slate-600/50' : 'divide-gray-100'}`}
                    >
                      {googleDriveFolders.map((folder) => (
                        <li
                          key={folder.id}
                          className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                            selectedFolder?.id === folder.id
                              ? isDark
                                ? 'bg-violet-500/20 border-l-4 border-violet-500'
                                : 'bg-indigo-50 border-l-4 border-indigo-600'
                              : isDark
                                ? 'hover:bg-slate-700/50'
                                : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleFolderSelect(folder)}
                          onDoubleClick={() => handleFolderClick(folder)}
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-5 h-5 text-yellow-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                            <span
                              className={`text-sm ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                            >
                              {folder.name}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleFolderClick(folder);
                            }}
                            className={`transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                            title={t('integrations.googleDrive.openFolder')}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Selected folder indicator */}
                {selectedFolder && (
                  <div
                    className={`mt-3 p-3 rounded-xl text-sm ${
                      isDark
                        ? 'bg-violet-500/20 text-violet-300'
                        : 'bg-indigo-100 text-indigo-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-indigo-600'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                      </svg>
                      <span className="font-medium">
                        {t('integrations.googleDrive.selectedFolder')}:
                      </span>
                    </div>
                    <div
                      className={`ml-6 font-semibold ${isDark ? 'text-violet-200' : 'text-indigo-900'}`}
                    >
                      {[
                        currentDrive.name,
                        ...folderPath.map((f) => f.name),
                        selectedFolder.name,
                      ].join(' / ')}
                    </div>
                  </div>
                )}
              </>
            )}

            <p
              className={`mt-2 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
            >
              {t('integrations.googleDrive.folderBrowserHelp')}
            </p>

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  setShowGoogleDriveForm(false);
                  setSelectedFolder(null);
                  setShowDriveSelector(true);
                }}
                className={`px-4 py-2 rounded-xl transition-colors ${
                  isDark
                    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConnectGoogleDrive}
                disabled={!selectedFolder}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('integrations.googleDrive.connectFolder')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* GitHub Integration */}
      <div
        className={`rounded-2xl p-4 ${
          isDark
            ? 'bg-slate-900/50 border border-slate-700/50'
            : 'bg-white border border-slate-200 shadow-sm'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center space-x-3">
            <svg
              className={`w-8 h-8 shrink-0 ${isDark ? 'fill-slate-100' : ''}`}
              viewBox="0 0 98 96"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M48.854 0C21.839 0 0 22 0 49.217c0 21.756 13.993 40.172 33.405 46.69 2.427.49 3.316-1.059 3.316-2.362 0-1.141-.08-5.052-.08-9.127-13.59 2.934-16.42-5.867-16.42-5.867-2.184-5.704-5.42-7.17-5.42-7.17-4.448-3.015.324-3.015.324-3.015 4.934.326 7.523 5.052 7.523 5.052 4.367 7.496 11.404 5.378 14.235 4.074.404-3.178 1.699-5.378 3.074-6.6-10.839-1.141-22.243-5.378-22.243-24.283 0-5.378 1.94-9.778 5.014-13.2-.485-1.222-2.184-6.275.486-13.038 0 0 4.125-1.304 13.426 5.052a46.97 46.97 0 0 1 12.214-1.63c4.125 0 8.33.571 12.213 1.63 9.302-6.356 13.427-5.052 13.427-5.052 2.67 6.763.97 11.816.485 13.038 3.155 3.422 5.015 7.822 5.015 13.2 0 18.905-11.404 23.06-22.324 24.283 1.78 1.548 3.316 4.481 3.316 9.126 0 6.6-.08 11.897-.08 13.526 0 1.304.89 2.853 3.316 2.364 19.412-6.52 33.405-24.935 33.405-46.691C97.707 22 75.788 0 48.854 0z"
                fill={isDark ? '#e2e8f0' : '#24292f'}
              />
            </svg>
            <div>
              <h3
                className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
              >
                {t('integrations.github.title')}
              </h3>
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
              >
                {t('integrations.github.description')}
              </p>
            </div>
          </div>

          {!getConnectionForProvider('github') ? (
            <button
              onClick={() => handleOAuthConnect('github')}
              className={`px-4 py-2 rounded-xl transition-colors ${
                isDark
                  ? 'bg-slate-700 text-slate-100 hover:bg-slate-600'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {t('integrations.github.connect')}
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              {integrations?.github && integrations.github.length > 0 && (
                <>
                  <button
                    onClick={() => handleSync('github')}
                    disabled={syncing === 'github'}
                    className={`px-3 py-1 text-sm rounded-lg disabled:opacity-50 transition-colors ${
                      isDark
                        ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {syncing === 'github'
                      ? t('integrations.status.syncing')
                      : t('integrations.actions.syncAll')}
                  </button>
                </>
              )}
              <button
                onClick={() => {
                  setShowGitHubForm(true);
                  fetchGitHubRepos();
                }}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all"
              >
                {integrations?.github && integrations.github.length > 0
                  ? t('integrations.github.addRepo')
                  : t('integrations.github.selectRepo')}
              </button>
            </div>
          )}
        </div>

        {/* Display connected GitHub repos */}
        {integrations?.github && integrations.github.length > 0 && (
          <div className="mt-3 space-y-3">
            {integrations.github.map((repo) => (
              <div
                key={repo.id}
                className={`p-3 rounded-xl text-sm ${
                  isDark
                    ? 'bg-slate-700/50 border border-slate-600/50'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <svg
                      className={`w-5 h-5 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                    <strong className={isDark ? 'text-slate-100' : ''}>
                      {repo.repo_full_name}
                    </strong>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${getSyncStatusBadge(repo.sync_status)}`}
                    >
                      {t(
                        `integrations.status.${repo.sync_status || 'pending'}`,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSync('github', repo.id)}
                      disabled={syncing === `github-${repo.id}`}
                      className={`px-2 py-1 text-xs rounded-lg disabled:opacity-50 transition-colors ${
                        isDark
                          ? 'border border-slate-600 text-slate-300 hover:bg-slate-600/50'
                          : 'border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {syncing === `github-${repo.id}`
                        ? t('integrations.status.syncing')
                        : t('integrations.actions.sync')}
                    </button>
                    <button
                      onClick={() => handleDisconnectGitHubRepo(repo.id)}
                      className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                        isDark
                          ? 'text-rose-400 border border-rose-500/50 hover:bg-rose-500/10'
                          : 'text-red-600 border border-red-300 hover:bg-red-50'
                      }`}
                    >
                      {t('integrations.actions.remove')}
                    </button>
                  </div>
                </div>
                <div
                  className={`grid grid-cols-2 gap-2 ${isDark ? 'text-slate-400' : 'text-gray-600'}`}
                >
                  <p>
                    <strong>{t('integrations.github.branch')}:</strong>{' '}
                    {repo.default_branch}
                  </p>
                  <p>
                    <strong>{t('integrations.github.syncedItems')}:</strong>{' '}
                    {repo.synced_items_count}
                  </p>
                </div>
                <p className={isDark ? 'text-slate-400' : 'text-gray-600'}>
                  <strong>{t('integrations.github.lastSync')}:</strong>{' '}
                  {formatLastSync(repo.last_sync_at)}
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {repo.sync_commits && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-800'}`}
                    >
                      {t('integrations.github.commits')}
                    </span>
                  )}
                  {repo.sync_prs && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${isDark ? 'bg-purple-500/20 text-purple-400' : 'bg-purple-100 text-purple-800'}`}
                    >
                      {t('integrations.github.pullRequests')}
                    </span>
                  )}
                  {repo.sync_issues && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-green-100 text-green-800'}`}
                    >
                      {t('integrations.github.issues')}
                    </span>
                  )}
                  {repo.sync_code && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${isDark ? 'bg-orange-500/20 text-orange-400' : 'bg-orange-100 text-orange-800'}`}
                    >
                      {t('integrations.github.code')}
                    </span>
                  )}
                </div>
                {repo.last_sync_error && (
                  <p
                    className={`mt-2 text-xs ${isDark ? 'text-rose-400' : 'text-red-600'}`}
                  >
                    <strong>{t('integrations.error')}:</strong>{' '}
                    {repo.last_sync_error}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {showGitHubForm && (
          <div
            className={`mt-4 p-4 rounded-xl ${
              isDark ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}
          >
            <h4
              className={`font-medium mb-3 ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
            >
              {t('integrations.github.selectRepository')}
            </h4>
            {loadingRepos ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label
                    className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                  >
                    {t('integrations.github.repository')}
                  </label>
                  <div className="relative">
                    {/* Search input */}
                    <div className="relative">
                      <input
                        type="text"
                        value={repoSearchQuery}
                        onChange={(e) => {
                          setRepoSearchQuery(e.target.value);
                          setShowRepoDropdown(true);
                        }}
                        onFocus={() => setShowRepoDropdown(true)}
                        placeholder={t('integrations.github.searchRepos')}
                        className={`w-full px-3 py-2 rounded-xl pr-10 ${
                          isDark
                            ? 'bg-slate-900/50 border border-slate-600 text-slate-100 placeholder-slate-500 focus:ring-violet-500 focus:border-violet-500'
                            : 'border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                        }`}
                      />
                      <svg
                        className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>

                    {/* Selected repo display */}
                    {selectedRepo && (
                      <div
                        className={`mt-2 flex items-center gap-2 p-2 rounded-xl ${
                          isDark
                            ? 'bg-violet-500/20 border border-violet-500/30'
                            : 'bg-indigo-50 border border-indigo-200'
                        }`}
                      >
                        <svg
                          className={`w-4 h-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                        </svg>
                        <span
                          className={`text-sm font-medium ${isDark ? 'text-violet-200' : 'text-indigo-900'}`}
                        >
                          {selectedRepo}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedRepo('');
                            setRepoSearchQuery('');
                          }}
                          className={`ml-auto ${isDark ? 'text-violet-400 hover:text-violet-300' : 'text-indigo-600 hover:text-indigo-800'}`}
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    )}

                    {/* Dropdown list */}
                    {showRepoDropdown && !selectedRepo && (
                      <div
                        className={`absolute z-10 w-full mt-1 rounded-xl shadow-lg max-h-64 overflow-y-auto ${
                          isDark
                            ? 'bg-slate-900 border border-slate-600'
                            : 'bg-white border border-gray-300'
                        }`}
                      >
                        {(() => {
                          const filteredRepos = gitHubRepos
                            .filter(
                              (repo) =>
                                !integrations?.github?.some(
                                  (g) => g.repo_full_name === repo.full_name,
                                ),
                            )
                            .filter(
                              (repo) =>
                                repo.full_name
                                  .toLowerCase()
                                  .includes(repoSearchQuery.toLowerCase()) ||
                                repo.name
                                  .toLowerCase()
                                  .includes(repoSearchQuery.toLowerCase()) ||
                                repo.description
                                  ?.toLowerCase()
                                  .includes(repoSearchQuery.toLowerCase()),
                            );

                          // Group by owner
                          const groupedRepos = filteredRepos.reduce(
                            (acc, repo) => {
                              const owner =
                                repo.owner || repo.full_name.split('/')[0];
                              if (!acc[owner]) {
                                acc[owner] = [];
                              }
                              acc[owner].push(repo);
                              return acc;
                            },
                            {} as Record<string, typeof gitHubRepos>,
                          );

                          const owners = Object.keys(groupedRepos).sort(
                            (a, b) =>
                              a.toLowerCase().localeCompare(b.toLowerCase()),
                          );

                          if (filteredRepos.length === 0) {
                            return (
                              <div
                                className={`px-3 py-4 text-sm text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                              >
                                {repoSearchQuery
                                  ? t('integrations.github.noMatchingRepos')
                                  : t('integrations.github.noReposAvailable')}
                              </div>
                            );
                          }

                          return owners.map((owner) => (
                            <div key={owner}>
                              <div
                                className={`px-3 py-2 text-xs font-semibold uppercase tracking-wide sticky top-0 flex items-center gap-2 ${
                                  isDark
                                    ? 'bg-slate-700 text-slate-400'
                                    : 'bg-gray-50 text-gray-600'
                                }`}
                              >
                                {groupedRepos[owner][0]?.owner_type ===
                                'Organization' ? (
                                  <svg
                                    className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" />
                                  </svg>
                                ) : (
                                  <svg
                                    className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                                  </svg>
                                )}
                                {owner}
                              </div>
                              {groupedRepos[owner].map((repo) => (
                                <button
                                  key={repo.full_name}
                                  type="button"
                                  onClick={() => {
                                    setSelectedRepo(repo.full_name);
                                    setRepoSearchQuery('');
                                    setShowRepoDropdown(false);
                                  }}
                                  className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors ${
                                    isDark
                                      ? 'hover:bg-slate-700'
                                      : 'hover:bg-gray-100'
                                  }`}
                                >
                                  <svg
                                    className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                                    fill="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                                  </svg>
                                  <div className="flex-1 min-w-0">
                                    <div
                                      className={`text-sm font-medium truncate ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                                    >
                                      {repo.name}
                                      {repo.private && (
                                        <span
                                          className={`ml-2 px-1.5 py-0.5 text-xs rounded ${
                                            isDark
                                              ? 'bg-slate-600 text-slate-300'
                                              : 'bg-gray-200 text-gray-600'
                                          }`}
                                        >
                                          {t('integrations.github.private')}
                                        </span>
                                      )}
                                    </div>
                                    {repo.description && (
                                      <div
                                        className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                                      >
                                        {repo.description}
                                      </div>
                                    )}
                                  </div>
                                </button>
                              ))}
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Click outside to close dropdown */}
                  {showRepoDropdown && (
                    <div
                      className="fixed inset-0 z-0"
                      onClick={() => setShowRepoDropdown(false)}
                    />
                  )}
                </div>

                <div>
                  <label
                    className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                  >
                    {t('integrations.github.syncOptions')}
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={gitHubOptions.sync_commits}
                        onChange={(e) =>
                          setGitHubOptions({
                            ...gitHubOptions,
                            sync_commits: e.target.checked,
                          })
                        }
                        className={`rounded ${isDark ? 'border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`}
                      />
                      <span
                        className={`ml-2 text-sm ${isDark ? 'text-slate-300' : ''}`}
                      >
                        {t('integrations.github.syncCommits')}
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={gitHubOptions.sync_prs}
                        onChange={(e) =>
                          setGitHubOptions({
                            ...gitHubOptions,
                            sync_prs: e.target.checked,
                          })
                        }
                        className={`rounded ${isDark ? 'border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`}
                      />
                      <span
                        className={`ml-2 text-sm ${isDark ? 'text-slate-300' : ''}`}
                      >
                        {t('integrations.github.syncPRs')}
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={gitHubOptions.sync_issues}
                        onChange={(e) =>
                          setGitHubOptions({
                            ...gitHubOptions,
                            sync_issues: e.target.checked,
                          })
                        }
                        className={`rounded ${isDark ? 'border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`}
                      />
                      <span
                        className={`ml-2 text-sm ${isDark ? 'text-slate-300' : ''}`}
                      >
                        {t('integrations.github.syncIssues')}
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={gitHubOptions.sync_code}
                        onChange={(e) =>
                          setGitHubOptions({
                            ...gitHubOptions,
                            sync_code: e.target.checked,
                          })
                        }
                        className={`rounded ${isDark ? 'border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`}
                      />
                      <span
                        className={`ml-2 text-sm ${isDark ? 'text-slate-300' : ''}`}
                      >
                        {t('integrations.github.syncCode')}
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => setShowGitHubForm(false)}
                    className={`px-4 py-2 rounded-xl transition-colors ${
                      isDark
                        ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                        : 'border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleConnectGitHub}
                    disabled={!selectedRepo}
                    className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all disabled:opacity-50"
                  >
                    {t('integrations.github.connectRepo')}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* OneDrive Integration */}
      <div
        className={`rounded-2xl p-4 ${
          isDark
            ? 'bg-slate-900/50 border border-slate-700/50'
            : 'bg-white border border-slate-200 shadow-sm'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center space-x-3">
            <svg
              className="w-8 h-8 shrink-0"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="#0078D4"
                d="M10.5 18.5c-3.038 0-5.5-2.462-5.5-5.5s2.462-5.5 5.5-5.5c1.595 0 3.03.679 4.037 1.764A6.48 6.48 0 0 1 17.5 8c3.038 0 5.5 2.462 5.5 5.5s-2.462 5.5-5.5 5.5H10.5z"
              />
              <path
                fill="#0364B8"
                d="M6.5 18.5c-2.485 0-4.5-2.015-4.5-4.5 0-2.348 1.801-4.28 4.102-4.482A5.487 5.487 0 0 1 10.5 7.5c1.37 0 2.636.502 3.612 1.338A6.477 6.477 0 0 1 17.5 8c.17 0 .338.007.504.02A4.993 4.993 0 0 0 14 5.5c-2.485 0-4.5 2.015-4.5 4.5 0 .17.01.338.027.504A4.48 4.48 0 0 0 6.5 9C4.015 9 2 11.015 2 13.5S4.015 18 6.5 18h0.001l-.001.5z"
              />
            </svg>
            <div>
              <h3
                className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
              >
                {t('integrations.onedrive.title')}
              </h3>
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
              >
                {t('integrations.onedrive.description')}
              </p>
            </div>
          </div>

          {!getConnectionForProvider('onedrive') ? (
            <button
              onClick={() => handleOAuthConnect('onedrive')}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all"
            >
              {t('integrations.onedrive.connect')}
            </button>
          ) : integrations?.onedrive ? (
            <div className="flex items-center space-x-2">
              <span
                className={`px-2 py-1 text-xs rounded-full ${getSyncStatusBadge(integrations.onedrive.sync_status)}`}
              >
                {t(
                  `integrations.status.${integrations.onedrive.sync_status || 'pending'}`,
                )}
              </span>
              <button
                onClick={() => handleSync('onedrive')}
                disabled={syncing === 'onedrive'}
                className={`px-3 py-1 text-sm rounded-lg disabled:opacity-50 transition-colors ${
                  isDark
                    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {syncing === 'onedrive'
                  ? t('integrations.status.syncing')
                  : t('integrations.actions.sync')}
              </button>
              <button
                onClick={() => handleDisconnectIntegration('onedrive')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  isDark
                    ? 'text-rose-400 border border-rose-500/50 hover:bg-rose-500/10'
                    : 'text-red-600 border border-red-300 hover:bg-red-50'
                }`}
              >
                {t('integrations.actions.disconnect')}
              </button>
            </div>
          ) : (
            <button
              onClick={openOneDriveBrowser}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all"
            >
              {t('integrations.onedrive.selectFolder')}
            </button>
          )}
        </div>

        {integrations?.onedrive && (
          <div
            className={`mt-3 p-3 rounded-xl text-sm ${
              isDark ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}
          >
            <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>
              <strong>{t('integrations.onedrive.folder')}:</strong>{' '}
              {integrations.onedrive.folder_path ||
                integrations.onedrive.folder_name ||
                integrations.onedrive.folder_id}
            </p>
            <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>
              <strong>{t('integrations.onedrive.documents')}:</strong>{' '}
              {integrations.onedrive.synced_documents_count}
            </p>
            <p className={isDark ? 'text-slate-300' : 'text-gray-700'}>
              <strong>{t('integrations.onedrive.lastSync')}:</strong>{' '}
              {formatLastSync(integrations.onedrive.last_sync_at)}
            </p>
            {integrations.onedrive.last_sync_error && (
              <p className={isDark ? 'text-rose-400' : 'text-red-600'}>
                <strong>{t('integrations.error')}:</strong>{' '}
                {integrations.onedrive.last_sync_error}
              </p>
            )}
          </div>
        )}

        {showOneDriveForm && (
          <div
            className={`mt-4 p-4 rounded-xl ${
              isDark ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}
          >
            <h4
              className={`font-medium mb-3 ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
            >
              {t('integrations.onedrive.browseAndSelect')}
            </h4>

            {showOneDriveDriveSelector ? (
              <div
                className={`rounded-xl overflow-hidden ${
                  isDark
                    ? 'bg-slate-900/50 border border-slate-600/50'
                    : 'border bg-white'
                }`}
              >
                <div
                  className={`p-3 border-b ${isDark ? 'bg-slate-700/50 border-slate-600/50' : 'bg-gray-50'}`}
                >
                  <span
                    className={`text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                  >
                    {t('integrations.onedrive.selectDrive')}
                  </span>
                </div>
                {loadingOneDriveFolders ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                  </div>
                ) : (
                  <ul
                    className={`divide-y ${isDark ? 'divide-slate-600/50' : 'divide-gray-100'}`}
                  >
                    {oneDriveDrives.map((drive) => (
                      <li
                        key={drive.id}
                        className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                          isDark ? 'hover:bg-slate-700/50' : 'hover:bg-gray-50'
                        }`}
                        onClick={() =>
                          handleOneDriveDriveSelect(drive.id, drive.name)
                        }
                      >
                        <div className="flex items-center gap-2">
                          <svg
                            className="w-5 h-5 text-blue-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                          </svg>
                          <span
                            className={`text-sm ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                          >
                            {drive.name}
                          </span>
                          {drive.type && (
                            <span
                              className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                            >
                              ({drive.type})
                            </span>
                          )}
                        </div>
                        <svg
                          className={`w-4 h-4 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <>
                <div
                  className={`flex items-center gap-2 mb-3 p-2 rounded-xl ${
                    isDark ? 'bg-slate-600/50' : 'bg-gray-100'
                  }`}
                >
                  <svg
                    className="w-5 h-5 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  <span
                    className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                  >
                    {currentOneDriveDrive.name}
                  </span>
                  <button
                    onClick={() => handleOneDriveBreadcrumbClick(-1)}
                    className={`ml-auto text-sm hover:underline ${
                      isDark
                        ? 'text-violet-400 hover:text-violet-300'
                        : 'text-indigo-600 hover:text-indigo-800'
                    }`}
                  >
                    {t('integrations.onedrive.changeDrive')}
                  </button>
                </div>

                <div
                  className={`flex items-center gap-1 text-sm mb-3 flex-wrap ${isDark ? 'text-slate-400' : 'text-gray-600'}`}
                >
                  <button
                    onClick={() => handleOneDriveBreadcrumbClick(-2)}
                    className={`hover:underline ${isDark ? 'hover:text-violet-400' : 'hover:text-indigo-600'}`}
                  >
                    {currentOneDriveDrive.name}
                  </button>
                  {oneDriveFolderPath.map((folder, index) => (
                    <React.Fragment key={folder.id}>
                      <span
                        className={isDark ? 'text-slate-500' : 'text-gray-400'}
                      >
                        /
                      </span>
                      <button
                        onClick={() => handleOneDriveBreadcrumbClick(index)}
                        className={`hover:underline ${isDark ? 'hover:text-violet-400' : 'hover:text-indigo-600'}`}
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                <div
                  className={`rounded-xl max-h-64 overflow-y-auto ${
                    isDark
                      ? 'bg-slate-900/50 border border-slate-600/50'
                      : 'border bg-white'
                  }`}
                >
                  {loadingOneDriveFolders ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                    </div>
                  ) : oneDriveFolders.length === 0 ? (
                    <div
                      className={`py-8 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      {t('integrations.onedrive.noFolders')}
                    </div>
                  ) : (
                    <ul
                      className={`divide-y ${isDark ? 'divide-slate-600/50' : 'divide-gray-100'}`}
                    >
                      {oneDriveFolders.map((folder) => (
                        <li
                          key={folder.id}
                          className={`flex items-center justify-between p-3 cursor-pointer transition-colors ${
                            selectedOneDriveFolder?.id === folder.id
                              ? isDark
                                ? 'bg-violet-500/20 border-l-4 border-violet-500'
                                : 'bg-indigo-50 border-l-4 border-indigo-600'
                              : isDark
                                ? 'hover:bg-slate-700/50'
                                : 'hover:bg-gray-50'
                          }`}
                          onClick={() => handleOneDriveFolderSelect(folder)}
                          onDoubleClick={() =>
                            handleOneDriveFolderClick(folder)
                          }
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-5 h-5 text-yellow-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                            </svg>
                            <span
                              className={`text-sm ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
                            >
                              {folder.name}
                            </span>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOneDriveFolderClick(folder);
                            }}
                            className={`transition-colors ${isDark ? 'text-slate-500 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {selectedOneDriveFolder && (
                  <div
                    className={`mt-3 p-3 rounded-xl text-sm ${
                      isDark
                        ? 'bg-violet-500/20 text-violet-300'
                        : 'bg-indigo-100 text-indigo-800'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <svg
                        className={`w-4 h-4 ${isDark ? 'text-violet-400' : 'text-indigo-600'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                      </svg>
                      <span className="font-medium">
                        {t('integrations.onedrive.selectedFolder')}:
                      </span>
                    </div>
                    <div
                      className={`ml-6 font-semibold ${isDark ? 'text-violet-200' : 'text-indigo-900'}`}
                    >
                      {[
                        currentOneDriveDrive.name,
                        ...oneDriveFolderPath.map((f) => f.name),
                        selectedOneDriveFolder.name,
                      ].join(' / ')}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex justify-end space-x-2 mt-4">
              <button
                onClick={() => {
                  setShowOneDriveForm(false);
                  setSelectedOneDriveFolder(null);
                }}
                className={`px-4 py-2 rounded-xl transition-colors ${
                  isDark
                    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConnectOneDrive}
                disabled={!selectedOneDriveFolder}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all disabled:opacity-50"
              >
                {t('integrations.onedrive.connectFolder')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Jira Integration */}
      <div
        className={`rounded-2xl p-4 ${
          isDark
            ? 'bg-slate-900/50 border border-slate-700/50'
            : 'bg-white border border-slate-200 shadow-sm'
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center space-x-3">
            <svg
              className="w-8 h-8 shrink-0"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="#2684FF"
                d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005z"
              />
              <path
                fill="#2684FF"
                d="M6.786 6.756h11.571a5.218 5.218 0 0 0-5.231-5.215h-2.13V-.516A5.215 5.215 0 0 0 5.782 4.7v11.481a1.005 1.005 0 0 0 1.004 1.005z"
                opacity=".8"
              />
              <path
                fill="#2684FF"
                d="M2.357 11.513h11.571a5.218 5.218 0 0 0-5.231-5.215H6.566V4.241A5.215 5.215 0 0 0 1.352 9.456v2.057z"
                opacity=".6"
              />
            </svg>
            <div>
              <h3
                className={`font-medium ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
              >
                {t('integrations.jira.title')}
              </h3>
              <p
                className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
              >
                {t('integrations.jira.description')}
              </p>
            </div>
          </div>

          {!getConnectionForProvider('jira') ? (
            <button
              onClick={() => handleOAuthConnect('jira')}
              className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all"
            >
              {t('integrations.jira.connect')}
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              {integrations?.jira && integrations.jira.length > 0 && (
                <button
                  onClick={() => handleSync('jira')}
                  disabled={syncing === 'jira'}
                  className={`px-3 py-1 text-sm rounded-lg disabled:opacity-50 transition-colors ${
                    isDark
                      ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                      : 'border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {syncing === 'jira'
                    ? t('integrations.status.syncing')
                    : t('integrations.actions.syncAll')}
                </button>
              )}
              <button
                onClick={openJiraBrowser}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all"
              >
                {integrations?.jira && integrations.jira.length > 0
                  ? t('integrations.jira.addProject')
                  : t('integrations.jira.selectProject')}
              </button>
            </div>
          )}
        </div>

        {/* Display connected Jira projects */}
        {integrations?.jira && integrations.jira.length > 0 && (
          <div className="mt-3 space-y-3">
            {integrations.jira.map((jiraInt) => (
              <div
                key={jiraInt.id}
                className={`p-3 rounded-xl text-sm ${
                  isDark
                    ? 'bg-slate-700/50 border border-slate-600/50'
                    : 'bg-gray-50 border border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <svg
                      className="w-5 h-5 text-blue-500"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005z" />
                    </svg>
                    <strong className={isDark ? 'text-slate-100' : ''}>
                      {jiraInt.jira_project_name ||
                        jiraInt.jira_project_key ||
                        jiraInt.site_name}
                    </strong>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full ${getSyncStatusBadge(jiraInt.sync_status)}`}
                    >
                      {t(
                        `integrations.status.${jiraInt.sync_status || 'pending'}`,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSync('jira', jiraInt.id)}
                      disabled={syncing === `jira-${jiraInt.id}`}
                      className={`px-2 py-1 text-xs rounded-lg disabled:opacity-50 transition-colors ${
                        isDark
                          ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                          : 'border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {syncing === `jira-${jiraInt.id}`
                        ? t('integrations.status.syncing')
                        : t('integrations.actions.sync')}
                    </button>
                    <button
                      onClick={() => handleDisconnectJiraProject(jiraInt.id)}
                      className={`px-2 py-1 text-xs rounded-lg transition-colors ${
                        isDark
                          ? 'text-rose-400 border border-rose-500/50 hover:bg-rose-500/10'
                          : 'text-red-600 border border-red-300 hover:bg-red-50'
                      }`}
                    >
                      {t('integrations.actions.remove')}
                    </button>
                  </div>
                </div>
                <div
                  className={`grid grid-cols-2 gap-2 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}
                >
                  <p>
                    <strong>{t('integrations.jira.site')}:</strong>{' '}
                    {jiraInt.site_name}
                  </p>
                  {jiraInt.jira_project_key && (
                    <p>
                      <strong>{t('integrations.jira.projectKey')}:</strong>{' '}
                      {jiraInt.jira_project_key}
                    </p>
                  )}
                </div>
                <p className={isDark ? 'text-slate-300' : 'text-gray-600'}>
                  <strong>{t('integrations.jira.lastSync')}:</strong>{' '}
                  {formatLastSync(jiraInt.last_sync_at)}
                </p>
                <div className="mt-2 flex flex-wrap gap-1">
                  {jiraInt.sync_issues && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        isDark
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {t('integrations.jira.issues')}
                    </span>
                  )}
                  {jiraInt.sync_sprints && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        isDark
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {t('integrations.jira.sprints')}
                    </span>
                  )}
                  {jiraInt.sync_epics && (
                    <span
                      className={`px-2 py-0.5 text-xs rounded ${
                        isDark
                          ? 'bg-violet-500/20 text-violet-400'
                          : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {t('integrations.jira.epics')}
                    </span>
                  )}
                </div>
                {jiraInt.last_sync_error && (
                  <p
                    className={`mt-2 text-xs ${isDark ? 'text-rose-400' : 'text-red-600'}`}
                  >
                    <strong>{t('integrations.error')}:</strong>{' '}
                    {jiraInt.last_sync_error}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {showJiraForm && (
          <div
            className={`mt-4 p-4 rounded-xl ${
              isDark ? 'bg-slate-700/50' : 'bg-gray-50'
            }`}
          >
            <h4
              className={`font-medium mb-3 ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
            >
              {t('integrations.jira.selectSiteAndProject')}
            </h4>

            {/* Site Selection */}
            <div className="mb-4">
              <label
                className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
              >
                {t('integrations.jira.site')}
              </label>
              {loadingJiraSites ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                </div>
              ) : (
                <select
                  value={selectedJiraSite?.id || ''}
                  onChange={(e) => {
                    const site = jiraSites.find((s) => s.id === e.target.value);
                    if (site) handleJiraSiteSelect(site);
                  }}
                  className={`w-full px-3 py-2 rounded-xl transition-colors ${
                    isDark
                      ? 'bg-slate-900/50 border border-slate-600 text-slate-100 focus:ring-violet-500 focus:border-violet-500'
                      : 'border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                >
                  <option value="">{t('integrations.jira.selectSite')}</option>
                  {jiraSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.name} ({site.url})
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Project Selection */}
            {selectedJiraSite && (
              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                >
                  {t('integrations.jira.project')}
                </label>
                {loadingJiraProjects ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-600"></div>
                  </div>
                ) : (
                  <select
                    value={selectedJiraProject?.id || ''}
                    onChange={(e) => {
                      const project = jiraProjects.find(
                        (p) => p.id === e.target.value,
                      );
                      setSelectedJiraProject(project || null);
                    }}
                    className={`w-full px-3 py-2 rounded-xl transition-colors ${
                      isDark
                        ? 'bg-slate-900/50 border border-slate-600 text-slate-100 focus:ring-violet-500 focus:border-violet-500'
                        : 'border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                    }`}
                  >
                    <option value="">
                      {t('integrations.jira.allProjects')}
                    </option>
                    {jiraProjects
                      .filter(
                        (project) =>
                          !integrations?.jira?.some(
                            (j) =>
                              j.jira_project_id === project.id &&
                              j.cloud_id === selectedJiraSite.id,
                          ),
                      )
                      .map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name} ({project.key})
                        </option>
                      ))}
                  </select>
                )}
              </div>
            )}

            {/* Sync Options */}
            {selectedJiraSite && (
              <div className="mb-4">
                <label
                  className={`block text-sm font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}
                >
                  {t('integrations.jira.syncOptions')}
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={jiraOptions.sync_issues}
                      onChange={(e) =>
                        setJiraOptions({
                          ...jiraOptions,
                          sync_issues: e.target.checked,
                        })
                      }
                      className={`rounded ${isDark ? 'border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`}
                    />
                    <span
                      className={`ml-2 text-sm ${isDark ? 'text-slate-300' : ''}`}
                    >
                      {t('integrations.jira.syncIssues')}
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={jiraOptions.sync_sprints}
                      onChange={(e) =>
                        setJiraOptions({
                          ...jiraOptions,
                          sync_sprints: e.target.checked,
                        })
                      }
                      className={`rounded ${isDark ? 'border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`}
                    />
                    <span
                      className={`ml-2 text-sm ${isDark ? 'text-slate-300' : ''}`}
                    >
                      {t('integrations.jira.syncSprints')}
                    </span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={jiraOptions.sync_epics}
                      onChange={(e) =>
                        setJiraOptions({
                          ...jiraOptions,
                          sync_epics: e.target.checked,
                        })
                      }
                      className={`rounded ${isDark ? 'border-slate-600 bg-slate-700 text-violet-500 focus:ring-violet-500' : 'border-gray-300 text-indigo-600 focus:ring-indigo-500'}`}
                    />
                    <span
                      className={`ml-2 text-sm ${isDark ? 'text-slate-300' : ''}`}
                    >
                      {t('integrations.jira.syncEpics')}
                    </span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowJiraForm(false);
                  setSelectedJiraSite(null);
                  setSelectedJiraProject(null);
                }}
                className={`px-4 py-2 rounded-xl transition-colors ${
                  isDark
                    ? 'border border-slate-600 text-slate-300 hover:bg-slate-700/50'
                    : 'border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConnectJira}
                disabled={!selectedJiraSite}
                className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl hover:from-violet-700 hover:to-purple-700 shadow-lg shadow-violet-500/25 transition-all disabled:opacity-50"
              >
                {t('integrations.jira.connectProject')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Connected Accounts Section */}
      {oauthConnections.length > 0 && (
        <div
          className={`rounded-2xl p-4 ${
            isDark
              ? 'bg-slate-900/50 border border-slate-700/50'
              : 'bg-white border border-slate-200 shadow-sm'
          }`}
        >
          <h3
            className={`font-medium mb-3 ${isDark ? 'text-slate-100' : 'text-gray-900'}`}
          >
            {t('integrations.connectedAccounts')}
          </h3>
          <div className="space-y-2">
            {oauthConnections.map((connection) => (
              <div
                key={connection.id}
                className={`flex items-center justify-between p-2 rounded-xl ${
                  isDark ? 'bg-slate-700/50' : 'bg-gray-50'
                }`}
              >
                <div>
                  <span
                    className={`font-medium capitalize ${isDark ? 'text-slate-100' : ''}`}
                  >
                    {connection.provider.replace('_', ' ')}
                  </span>
                  {connection.account_email && (
                    <span
                      className={`ml-2 text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
                    >
                      ({connection.account_email})
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleOAuthDisconnect(connection.id)}
                  className={`text-sm transition-colors ${
                    isDark
                      ? 'text-rose-400 hover:text-rose-300'
                      : 'text-red-600 hover:text-red-800'
                  }`}
                >
                  {t('integrations.actions.disconnectAccount')}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectIntegrations;
